import React, { useState } from "react";
import { Row, Col, Typography, Space, theme, message, Spin, Card } from "antd";
import { ThunderboltOutlined, FireOutlined } from "@ant-design/icons";
import styles from "../styles/Billing.module.css";
import {
    useCreateTopup,
    useVerifyTopup,
} from "../../_actions/mutations/subscription";

const { Title, Text } = Typography;

const RAZORPAY_KEY = "rzp_live_RiGIXNKU65WT3j";

const PRESETS = [
    { key: "topup_5", price: 5, tokens: 3 },
    { key: "topup_100", price: 100, tokens: 60 },
    { key: "topup_200", price: 200, tokens: 120 },
    { key: "topup_500", price: 500, tokens: 300, popular: true },
    { key: "topup_1000", price: 1000, tokens: 600, bestValue: true },
];

function loadRazorpayScript() {
    return new Promise((resolve) => {
        if (window.Razorpay) {
            resolve(true);
            return;
        }
        const script = document.createElement("script");
        script.src = "https://checkout.razorpay.com/v1/checkout.js";
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
    });
}

const TokenTopUpCard = ({ orgId, userId }) => {
    const { token } = theme.useToken();
    const [loadingPreset, setLoadingPreset] = useState(null);

    const verifyTopup = useVerifyTopup(
        (data) => {
            setLoadingPreset(null);
            if (data.status === "topup_successful") {
                message.success(
                    `${data.tokens_added} tokens added! New balance: ${data.new_balance}`
                );
            } else if (data.status === "already_processed") {
                message.info("This payment was already processed.");
            }
        },
        (error) => {
            setLoadingPreset(null);
            message.error(
                error?.response?.data?.detail ||
                    "Payment verification failed. Contact support."
            );
        }
    );

    const createTopup = useCreateTopup(
        async (data) => {
            const loaded = await loadRazorpayScript();
            if (!loaded) {
                setLoadingPreset(null);
                message.error(
                    "Razorpay failed to load. Check your connection."
                );
                return;
            }

            const options = {
                key: RAZORPAY_KEY,
                order_id: data.order_id,
                amount: data.amount * 100,
                currency: "INR",
                name: "Qureal AI",
                description: `Add ${data.tokens_to_add} tokens`,
                image: "https://api.qureal.com/passets/c90e4024-5d6a-4df0-b713-9362a6083eb9.png",
                handler: (response) => {
                    verifyTopup.mutate({
                        razorpay_payment_id: response.razorpay_payment_id,
                        razorpay_order_id: response.razorpay_order_id,
                        razorpay_signature: response.razorpay_signature,
                    });
                },
                modal: {
                    ondismiss: () => setLoadingPreset(null),
                },
                theme: { color: token.colorPrimary },
            };

            const rzp = new window.Razorpay(options);
            rzp.on("payment.failed", () => {
                setLoadingPreset(null);
                message.error("Payment failed. Please try again.");
            });
            rzp.open();
        },
        (error) => {
            setLoadingPreset(null);
            message.error(
                error?.response?.data?.detail ||
                    "Failed to create top-up order."
            );
        }
    );

    const handleTopUp = (preset) => {
        if (loadingPreset) return;
        setLoadingPreset(preset.key);
        createTopup.mutate({
            org_id: orgId,
            account_id: userId,
            preset: preset.key,
        });
    };

    return (
        <Space direction="vertical" size={20} style={{ width: "100%" }}>
            <div>
                <Title level={5} style={{ margin: "0 0 4px" }}>
                    Buy Extra Tokens
                </Title>
                <Text type="secondary" style={{ fontSize: 13 }}>
                    Credited instantly · ₹1.67 per token
                </Text>
            </div>

            <Row gutter={[10, 10]}>
                {PRESETS.map((preset) => {
                    const isLoading = loadingPreset === preset.key;
                    const isDisabled = !!loadingPreset && !isLoading;
                    const isHighlighted = preset.popular || preset.bestValue;

                    return (
                        <Col xs={12} sm={8} key={preset.key}>
                            <div
                                className={styles.tokenPackItem}
                                onClick={() => {
                                    if (!isDisabled && !isLoading)
                                        handleTopUp(preset);
                                }}
                                style={{
                                    position: "relative",
                                    border: `1.5px solid ${
                                        isHighlighted
                                            ? token.colorPrimary
                                            : token.colorBorderSecondary
                                    }`,
                                    borderRadius: token.borderRadiusLG,
                                    padding: "18px 10px 14px",
                                    textAlign: "center",
                                    cursor: isDisabled
                                        ? "not-allowed"
                                        : "pointer",
                                    background: isHighlighted
                                        ? token.colorPrimaryBg
                                        : token.colorBgContainer,
                                    opacity: isDisabled ? 0.45 : 1,
                                    userSelect: "none",
                                }}
                            >
                                {(preset.popular || preset.bestValue) && (
                                    <div
                                        style={{
                                            position: "absolute",
                                            top: -9,
                                            left: "50%",
                                            transform: "translateX(-50%)",
                                            background: preset.popular
                                                ? token.colorPrimary
                                                : token.colorSuccess,
                                            color: "#fff",
                                            fontSize: 10,
                                            fontWeight: 700,
                                            padding: "1px 8px",
                                            borderRadius: 20,
                                            whiteSpace: "nowrap",
                                            letterSpacing: "0.3px",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 3,
                                        }}
                                    >
                                        {preset.popular ? (
                                            <>
                                                <FireOutlined
                                                    style={{ fontSize: 9 }}
                                                />
                                                Popular
                                            </>
                                        ) : (
                                            "Best Value"
                                        )}
                                    </div>
                                )}

                                <div
                                    style={{
                                        height: 28,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        marginBottom: 6,
                                    }}
                                >
                                    {isLoading ? (
                                        <Spin size="small" />
                                    ) : (
                                        <ThunderboltOutlined
                                            style={{
                                                fontSize: 18,
                                                color: isHighlighted
                                                    ? token.colorPrimary
                                                    : token.colorTextTertiary,
                                            }}
                                        />
                                    )}
                                </div>

                                <Text
                                    strong
                                    style={{
                                        fontSize: 18,
                                        display: "block",
                                        color: isHighlighted
                                            ? token.colorPrimary
                                            : token.colorText,
                                        lineHeight: 1.2,
                                        marginBottom: 3,
                                    }}
                                >
                                    ₹{preset.price}
                                </Text>
                                <Text
                                    type="secondary"
                                    style={{ fontSize: 12, display: "block" }}
                                >
                                    {preset.tokens} tokens
                                </Text>
                            </div>
                        </Col>
                    );
                })}
            </Row>
        </Space>
    );
};

export default TokenTopUpCard;
